import { CanvasObject }           from './canvas.object';
import { Logger }                 from '@/util/logger';
import { VisualFactory }          from './visuals/visual.factory';
import { Text }                   from './visuals/text'
import { Track }                  from '@/models/scenario/track';
import { Orientation }            from './orientation';
import { FontWeight }             from '@/helpers/fonts/font.weight';
import { FontVariant }            from '@/helpers/fonts/font.variant';
import { FontStyle }              from '@/helpers/fonts/font.style';
import { Port }                   from './port';
import { EventHandlerInterface }  from './events/event.handler.interface';
import { HighLightableInterface } from './high.lightable.interface';
import { HighLightInterface }     from './high.light.interface';
import { HighLighterInterface }   from './high.lighter.interface';

/**
 *
 * @class TrackLabel
 *
 * label for tracks, can be east, west or junction tracks depending on
 * which block this label is a child of.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      January 2024
 *
 */
export class TrackLabel
  extends CanvasObject
  implements EventHandlerInterface, HighLightableInterface {

  /** the current highlight if there is one */
  public highLightStyle: HighLightInterface|null = null;

  /** the highlighter we can use for doing hover/click highlights */
  public highlighter: HighLighterInterface|null  = null;

  /* styling */

  /** the padding inside the outline */
  public padding: number                         = 8;

  /** the spacing between tracks */
  public spacing: number                         = 8;

  /** color */
  public color: string                           = 'white';

  /** the color of connector port */
  public portColor: string                       = 'lightgrey';

  /** transparency */
  public opacity: number                         = 1.0;

  /** visibility */
  public visible: boolean                        = true;

  /** the font size (height / line height) */
  public size: number                            = 14;

  /** the direction the label points (to where a connector can attach) */
  public orientation: Orientation                = Orientation.RIGHT;

  /** the position of this label relative to the first track and the parent block */
  public order: number                           = 0;

  /** the maximum width of the labels */
  public titlesWidth: number                     = 64;

  /** the maximum height of the labels (should all be the same */
  public titlesHeight: number                    = 14;

  /** leave room for something to be above our track label array */
  public verticalOffset: number                  = 0;

  /** the horizontal offset (from top/left) to where the port connector starts */
  private portOffsetX: number                    = 0;

  /** the veritical offset (from top/left) to where the port connector starts */
  private portOffsetY: number                    = 0;

  /** the track this label is for */
  public track: Track | null                     = null;

  /** background rectangle for this block */
  public label: Text | null                      = null;

  /** saved style when we have a style override in play */
  public savedStyle: {
    color?:   string,
    opacity?: number
  } = {};

  /**
   *
   * @param parent      CanvasObject normally should be a link block
   * @param highlighter The HighLightManager we can use for highlighting and style overrides
   * @param config      Partial<TrackLabel> override the style options
   *
   */
  constructor(
    parent: CanvasObject,
    highlighter: HighLighterInterface|null,
    config: Partial<TrackLabel> = {}) {

    super(parent.canvas(), true);

    this.highlighter = highlighter;

    /* link up with parent */

    parent.addChildren(this);

    /* use any overrides */

    Object.assign(this, config);

    Logger.debug(`TrackLabel ctor() config: `, config);

    this.label = VisualFactory.buildText(this.labelConfig());

    if (this.label) {

      /* save the handle, so we can use it to manipulate the visual later */

      this.label.handle = this.canvas()?.addShape(this.label, this);

      this.boundUpdate({
        x:      this.label.x      ?? 0,
        y:      this.label.y      ?? 0,
        width:  this.label.width  ?? 10,
        height: this.label.height ?? 10
      });
    }

    /* dangle a port off the end... */

    new Port(this, {
      orientation: this.orientation,
      offsetX:     this.portOffsetX,
      offsetY:     this.portOffsetY,
      color:       this.portColor,
      padding:     this.padding
    });

  }

  /** convenience to format the text label for a track */
  public static trackName(track: Track, idFirst: boolean = false): string {

    if (idFirst) {

      return  '(' + track._id + ') ' + Track.typeToString(track._type) + track._track;
    }

    return Track.typeToString(track._type) + track._track + ' (' + track._id + ')';
  }

  /** generate the configuration for the title text label */
  private labelConfig(): Partial<Text> {

    /*
     * the tracks labels are an array that is distributed evenly along
     * one side of the parent block.  They also have a direction,
     * the port for connecting must be on the outside edge of the parent
     * block, so that connections can be...connected. :)
     *
     * If the parent block is vertical (east/east side) the title will
     * be at the top and then track labels below that.  That is, everything
     * is stacked.  If the parent block is horizontal (the junction block),
     * then the title will be above or below the array of track labels,
     * so we can just center the track array within the parent width.
     *
     * Inside the bound of the tracks; the order is critical, it's the
     * index for how far to offset a given track within the array of
     * track labels.  Tracks don't know about each other...they only know
     * their order, and the offset that implies.
     *
     */

    let x: number              = this.parent()?.x ?? 0;
    let y: number              = this.parent()?.y ?? 0;
    let width: number          = 10;
    let height: number         = this.size;
    let text: string           = 'X0';
    let color: string          = this.color;
    let family: string         = 'Arial';
    let size: number           = this.size;
    let weight: FontWeight     = FontWeight.BOLD;
    let variation: FontVariant = FontVariant.NORMAL;
    let style: FontStyle       = FontStyle.NORMAL;
    let visible: boolean       = true;
    let opacity: number        = 1.0;
    let rotation: number       = 0.0;

    /* set the track name */

    if (this.track) {

      text = TrackLabel.trackName(this.track, this.orientation == Orientation.RIGHT);
    }

    /* figure out placement of the track (x,y) ... */

    let left: number = x;
    let top: number  = y;
    let w: number    = this.parent()?.width  ?? 0;
    let h: number    = this.parent()?.height ?? 0;

    if (this.orientation == Orientation.LEFT || this.orientation == Orientation.RIGHT) {

      if (this.orientation == Orientation.RIGHT ) {

        /* point right to center */

        this.portOffsetX = left + w - this.padding;

        x                = this.portOffsetX - this.titlesWidth;

      } else {

        /* point left to center */

        x                 = left + this.padding;

        this.portOffsetX = x;
      }

      y                = top  + this.verticalOffset + (this.size + this.spacing) * this.order;

      this.portOffsetY = y + (this.size / 2);

    } else {

      if (this.orientation == Orientation.TOP ) {

        this.portOffsetY = top + h - this.padding;

        y                = this.portOffsetY - this.titlesWidth;

        rotation         = 90;

      } else {

        y                = top + h - this.padding;

        this.portOffsetY = y;

        rotation         = -90;

      }

      x = left + this.padding + (w/2) - (this.titlesHeight / 2) + (this.size + this.spacing) * this.order;

      this.portOffsetX = x + (this.size / 2);
    }

    /* pass back the specification for the label */

    return {
      x,
      y,
      width,
      height,
      text,
      color,
      family,
      size,
      weight,
      variation,
      style,
      visible,
      opacity,
      rotation
    };
  }

  /** upon return object should have updated its layout or geometry as needed. */
  public layout(): CanvasObject {

    /*
     * the screen geometry has changed somehow, so we have to recalculate our layout and
     * then get the canvas visuals to update in place.
     *
     */

    let handle: any = this.label?.handle ?? null;

    if (!handle) {

      /* nothing to do layout on yet */

      return this;
    }

    this.label        = VisualFactory.buildText(this.labelConfig());
    this.label.handle = handle;

    this.boundUpdate({
      x:      this.label.x      ?? 0,
      y:      this.label.y      ?? 0,
      width:  this.label.width  ?? 10,
      height: this.label.height ?? 10
    });

    this.canvas()?.updateShape(this.label);

    /* update the port dangling off the end ... */

    this.children().forEach((item: CanvasObject): void => {

      (item as Port).offsetX = this.portOffsetX;
      (item as Port).offsetY = this.portOffsetY;

      item.layout();
    });

    return this;
  }

  /** handle mouse over (hover start) */
  onMouseOver(): void {

    this.highlighter?.hovering(this);
  }

  /** handle mouse out (hover end) */
  onMouseOut(): void {

    this.highlighter?.leaving(this);
  }

  /** handle a click */
  onClick(): void {

    this.highlighter?.clicked(this);

  }

  /** handle a double click */
  onDoubleClick(): void {


  }

  /**
   * enable or disable highlighting.  When not null, set highLightStyle to
   * the new style, and object should apply any applicable style overrides.
   * When null, highLightStyle should be cleared, and the object should
   * revert to its normal styles.
   *
   * @param style the highlight to apply, or clear the current highlight.
   */
  highLight(style: HighLightInterface|null): void {

    let highlighter: HighLighterInterface|null = this.canvas()?.highlighter ?? null;

    if (!highlighter) {

      /* no highlighter to use */

      return ;
    }

    /* are we removing the override? */

    if (!style) {

      /* revert */

      if (this.savedStyle.color) {

        this.color = this.savedStyle.color;
      }

      if (this.savedStyle.opacity) {

        this.opacity = this.savedStyle.opacity;
      }

      this.savedStyle = {};

      /* update the visuals */

      this.layout();

      return ;
    }

    /* save the current styling so we can revert back to it later */

    this.savedStyle = {
      color:   this.color,
      opacity: this.opacity
    }

    if (style.color) {

      this.color = style.color;
    }

    if (style.opacity) {

      this.opacity = style.opacity;
    }

    /* update the visuals */

    this.layout();

  }

}
